import i18n from '@/i18n';
import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';
import {
  resolveVideoSpacePlayUrl,
} from '@/pages/ipe/workbench/utils/downloadVideoSpaceVideo';
import {
  pickVideoSpaceSignOptions,
  resolveVideoSpacePosterUrl,
} from '@/pages/ipe/workbench/utils/videoSpaceDisplayAccess';
import {
  isLikelyStillImageUrl,
  isVideoFileUrl,
} from '@/pages/ipe/workbench/utils/videoStudioDetailUtils';

/** 与发品主图视频本地上传一致的 accept */
export const PUBLISH_VIDEO_ACCEPT =
  'video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.m4v,.webm';

/** Temu 主图视频允许比例：1:1 / 3:4 / 16:9 */
export const TEMU_PUBLISH_VIDEO_RATIO_HINT =
  '主图视频：可选；MP4/MOV/M4V/WebM；≤20MB；画面比例须为 1:1、3:4 或 16:9（建议 1:1 或 3:4）。支持本地文件或视频空间上传。';

const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|avi)(\?|#|$)/i;
const VIDEO_MIME_RE = /^video\//i;
const TEMU_VIDEO_RATIOS = [1, 3 / 4, 16 / 9];

/** 读取本地视频宽高 */
export function probeLocalVideoSize(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('未选择视频文件'));
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute('src');
      video.load();
    };
    video.onloadedmetadata = () => {
      const width = Number(video.videoWidth) || 0;
      const height = Number(video.videoHeight) || 0;
      cleanup();
      if (width <= 0 || height <= 0) {
        reject(new Error('无法读取视频尺寸'));
        return;
      }
      resolve({ width, height });
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('视频元数据读取失败'));
    };
    video.src = objectUrl;
  });
}

export function isTemuAllowedVideoRatio(width, height, tolerance = 0.03) {
  const w = Number(width);
  const h = Number(height);
  if (!(w > 0) || !(h > 0)) return false;
  const ratio = w / h;
  return TEMU_VIDEO_RATIOS.some((target) => Math.abs(ratio - target) <= tolerance);
}

/** 校验本地视频是否符合 Temu 主图比例；不合规则抛出中文错误 */
export async function assertTemuPublishVideoRatio(file) {
  const { width, height } = await probeLocalVideoSize(file);
  if (!isTemuAllowedVideoRatio(width, height)) {
    throw new Error(
      `视频画面比例不符合要求（当前约 ${width}:${height}），须为 1:1、3:4 或 16:9（建议 1:1 或 3:4）`,
    );
  }
  return { width, height };
}

export function isAllowedPublishVideoFormat(item = {}) {
  const mime = String(
    item.mimeType
    || item.contentType
    || item.type
    || item.fileMime
    || '',
  ).trim();
  if (VIDEO_MIME_RE.test(mime)) return true;

  const candidates = [
    item.name,
    item.fileName,
    item.filename,
    item.outputVideoUrl,
    item.videoUrl,
    item.url,
    item.src,
    item.previewUrl,
  ];
  for (const value of candidates) {
    const text = String(value || '').trim();
    if (!text) continue;
    if (VIDEO_EXT_RE.test(text) || isVideoFileUrl(text)) return true;
  }
  return false;
}

/** 视频空间行 → LibraryModal 可渲染条目（保留播放地址，封面单独字段） */
export function mapVideoSpaceRowToLibraryItem(video = {}) {
  const poster = resolveVideoSpacePosterUrl(video);
  const playUrl = resolveVideoSpacePlayUrl(video);
  const name = String(video.name || video.fileName || video.filename || 'video.mp4').trim() || 'video.mp4';
  // 勿把 url/src 改成海报：否则播放解析会落到 jpg，上传会失败；无封面时由卡片用 <video> 兜底
  return {
    ...video,
    id: video.id,
    name,
    outputVideoUrl: playUrl || video.outputVideoUrl || '',
    posterUrl: poster || video.posterUrl || '',
    thumbnailUrl: poster || video.thumbnailUrl || '',
    coverUrl: poster || video.coverUrl || '',
    _mediaKind: 'video',
    _playUrl: playUrl || '',
    _posterUrl: poster || '',
  };
}

export function filterPublishVideoLibraryRows(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map(mapVideoSpaceRowToLibraryItem)
    .filter((row) => row?.id != null && isAllowedPublishVideoFormat(row));
}

/** 解析可上传的成片地址（跳过海报/静图） */
function resolveUploadPlayUrl(video) {
  const preferred = String(video?._playUrl || '').trim();
  if (preferred && !isLikelyStillImageUrl(preferred)) return preferred;

  const fromHelper = resolveVideoSpacePlayUrl(video);
  if (fromHelper && !isLikelyStillImageUrl(fromHelper)) return fromHelper;

  for (const value of [
    video?.outputVideoUrl,
    video?.videoUrl,
    video?.url,
    video?.src,
  ]) {
    const text = String(value || '').trim();
    if (!text || text.startsWith('blob:')) continue;
    if (isLikelyStillImageUrl(text) && !isVideoFileUrl(text)) continue;
    return text;
  }
  return '';
}

/** 视频空间记录 → File（与下载一致：签名后拉取 blob） */
export async function videoSpaceRecordToUploadFile(video, fallbackName = 'video.mp4') {
  let playUrl = resolveUploadPlayUrl(video);
  let record = video;

  // 列表项偶发缺播放地址：按 id 拉详情再解析（与 downloadVideoSpaceVideo 对齐）
  if (!playUrl && video?.id) {
    const { videoSpaceAPI } = await import('@/utils/api');
    const res = await videoSpaceAPI.getById(String(video.id));
    const remote = res?.video ?? res?.data ?? res;
    if (remote && typeof remote === 'object') {
      record = { ...video, ...remote, _mediaKind: 'video' };
      playUrl = resolveUploadPlayUrl(record);
    }
  }

  if (!playUrl) {
    throw new Error(i18n.t('workbench:listingVideo.videoNoUrl'));
  }

  const blob = await resolveImageUrlToBlob(playUrl, pickVideoSpaceSignOptions(record, playUrl));
  if (!blob) {
    throw new Error(i18n.t('workbench:ipeUtilError.cannotReadVideoRetry'));
  }
  if (blob.type && /^image\//i.test(blob.type)) {
    throw new Error(i18n.t('workbench:ipeUtilError.videoNotFileReselect'));
  }
  const rawName = String(record?.name || record?.fileName || record?.filename || fallbackName).trim()
    || fallbackName;
  const fileName = VIDEO_EXT_RE.test(rawName) ? rawName : `${rawName.replace(/\.[^.]+$/, '') || 'video'}.mp4`;
  const type = blob.type && VIDEO_MIME_RE.test(blob.type) ? blob.type : 'video/mp4';
  return new File([blob], fileName, { type, lastModified: Date.now() });
}
